import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiDownload, FiEdit2, FiCheck, FiX, FiImage, FiClock, FiHardDrive, FiEye } from 'react-icons/fi';
import { apiFetch } from '../utils/api';
import { showToast } from './NotificationTray';
import BatchUploader from './SharedUploadSession';
import useDetachedUploadRefresh from '../hooks/useDetachedUploadRefresh';
import { useUpload } from '../contexts/UploadContext';
import PageHero from './PageHero';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function daysSince(dateStr) {
  if (!dateStr) return 0;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function durationLabel(dateStr) {
  if (!dateStr) return '';
  const days = daysSince(dateStr);
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  if (days < 30) return `${days} days`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? '1 month' : `${months} months`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem > 0 ? `${years}y ${rem}mo` : `${years} year${years > 1 ? 's' : ''}`;
}

export default function VaultCollection() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [collection, setCollection] = useState(null);
  const [files, setFiles] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [showUploader, setShowUploader] = useState(false);
  const uploadContext = useUpload();



  // Retrieval state
  const [retrievals, setRetrievals] = useState([]);
  const [requestingRetrieval, setRequestingRetrieval] = useState(false);

  // Lightbox
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const fetchCollection = useCallback(async (page = 1) => {
    try {
      const res = await apiFetch(`/api/vault/collections/${id}?page=${page}&limit=50`);
      if (!res.ok) {
        if (res.status === 404) return navigate('/vault');
        return;
      }
      const data = await res.json();
      setCollection(data.collection);
      setFiles(data.files);
      setPagination(data.pagination);
      setEditName(data.collection.name);
    } catch (err) {
      console.error('Failed to fetch collection:', err);
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchRetrievals = useCallback(async () => {
    try {
      const res = await apiFetch('/api/vault/retrieval-requests');
      if (res.ok) {
        const data = await res.json();
        setRetrievals(data.retrieval_requests?.filter(r => {
          const ids = typeof r.collection_ids === 'string' ? JSON.parse(r.collection_ids) : r.collection_ids;
          return ids.includes(id);
        }) || []);
      }
    } catch (err) { /* ignore */ }
  }, [id]);

  useEffect(() => { fetchCollection(); fetchRetrievals(); }, [fetchCollection, fetchRetrievals]);

  // Save name edit
  const saveName = async () => {
    if (!editName.trim() || editName.trim() === collection.name) {
      setEditing(false);
      return;
    }
    try {
      const res = await apiFetch(`/api/vault/collections/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editName.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setCollection(data.collection);
        showToast('Name updated', 'success');
      }
    } catch (err) {
      showToast('Failed to update name', 'error');
    }
    setEditing(false);
  };

  // Request retrieval
  const handleRequestRetrieval = async () => {
    if (!confirm('Request retrieval of all files in this collection? This typically takes 24-72 hours. You\'ll receive an email when ready.')) return;
    setRequestingRetrieval(true);
    try {
      const res = await apiFetch('/api/vault/retrieval-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ collection_ids: [id] }),
      });
      if (res.ok) {
        showToast('Retrieval request submitted', 'success');
        fetchRetrievals();
      } else {
        const err = await res.json();
        showToast(err.error || 'Failed to request retrieval', 'error');
      }
    } catch (err) {
      showToast('Failed to request retrieval', 'error');
    } finally {
      setRequestingRetrieval(false);
    }
  };

  // Processing status
  const [processingStatus, setProcessingStatus] = useState(null);

  // Handle upload completion from BatchUploader, don't close, just refresh grid
  useDetachedUploadRefresh(detail => String(detail.vaultCollectionId) === String(id), () => fetchCollection());

  const handleUploadComplete = useCallback((result) => {
    fetchCollection();
  }, [fetchCollection]);

  // Poll for proxy generation updates when files are still processing
  useEffect(() => {
    const hasProcessing = files.some(f => !f.proxy_url && (f.status === 'processing_proxy' || f.status === 'pending'));
    if (!hasProcessing) return;

    const poll = async () => {
      try {
        const res = await apiFetch(`/api/vault/collections/${id}/processing-status`);
        if (res.ok) {
          const data = await res.json();
          setProcessingStatus(data);
          // If processing is done, refresh the full collection to get proxy URLs
          if (data.processing === 0) {
            fetchCollection();
          }
        }
      } catch {}
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [files, id, fetchCollection]);

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-32" />
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-6">
            {[1,2,3,4,5,6].map(i => <div key={i} className="aspect-square bg-gray-200 rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!collection) return null;

  const hasActiveRetrieval = retrievals.some(r => r.status === 'pending' || r.status === 'processing');
  const completedRetrieval = retrievals.find(r => r.status === 'complete' && r.download_url);

  return (
    <>
      {/* Hero */}
      <PageHero
        eyebrow={<Link to="/vault" className="inline-flex items-center gap-1 hover:underline"><FiArrowLeft size={12} /> Photo Vault</Link>}
        title={editing ? (
          <span className="flex items-center gap-2">
            <input
              value={editName}
              onChange={e => setEditName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditing(false); }}
              className="border-b-2 border-blue-500 outline-none bg-transparent"
              autoFocus
            />
            <button onClick={saveName} className="text-green-500 hover:text-green-700"><FiCheck size={20} /></button>
            <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600"><FiX size={20} /></button>
          </span>
        ) : (
          <span className="flex items-center gap-2">
            {collection.name}
            <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-gray-600"><FiEdit2 size={16} /></button>
          </span>
        )}
        stats={[
          { label: 'Files', value: collection.total_files.toLocaleString() },
          { label: 'Archive size', value: formatBytes(collection.total_bytes) },
          { label: 'Stored', value: durationLabel(collection.archived_at || collection.created_at) },
        ]}
      >
        <div className="flex items-center gap-2">
          <button
            onClick={() => {uploadContext?.maximizePanel();setShowUploader(true);}}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            <FiUpload size={14} />
            Upload
          </button>
          <button
            onClick={handleRequestRetrieval}
            disabled={hasActiveRetrieval || requestingRetrieval || collection.total_files === 0}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors"
          >
            <FiDownload size={14} />
            {hasActiveRetrieval ? 'Retrieval Pending...' : 'Request Retrieval'}
          </button>
        </div>
      </PageHero>

      {/* Detail stats bar */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-4 px-1 py-2 border-b border-gray-100">
          <span className="flex items-center gap-1">
            <FiImage size={12} />
            <strong className="text-gray-700">{collection.total_files}</strong> originals
          </span>
          <span className="flex items-center gap-1">
            <FiHardDrive size={12} />
            Archive: <strong className="text-gray-700">{formatBytes(collection.total_bytes)}</strong>
          </span>
          {collection.proxy_bytes > 0 && (
            <span className="flex items-center gap-1">
              <FiEye size={12} />
              Previews: <strong className="text-gray-700">{formatBytes(collection.proxy_bytes)}</strong>
            </span>
          )}
          <span className="flex items-center gap-1">
            <FiClock size={12} />
            Created {new Date(collection.created_at).toLocaleDateString()}
          </span>
          {collection.archived_at && (
            <span className="flex items-center gap-1">
              Archived {new Date(collection.archived_at).toLocaleDateString()}
              ({durationLabel(collection.archived_at)} ago)
            </span>
          )}
          <span className={`px-2 py-0.5 rounded-full font-medium ${
            collection.status === 'archived' ? 'bg-green-100 text-green-700'
            : collection.status === 'active' ? 'bg-blue-100 text-blue-700'
            : 'bg-yellow-100 text-yellow-700'
          }`}>
            {collection.status}
          </span>
        </div>
      </div>

    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
      {/* Retrieval status */}
      {completedRetrieval && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div>
            <span className="font-medium text-green-800">Retrieval ready!</span>
            <span className="text-sm text-green-600 ml-2">
              Expires {new Date(completedRetrieval.download_expires_at).toLocaleString()}
            </span>
          </div>
          <a
            href={completedRetrieval.download_url}
            className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FiDownload size={14} />
            Download
          </a>
        </div>
      )}

      {hasActiveRetrieval && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <span className="font-medium text-yellow-800">Retrieval in progress...</span>
          <span className="text-sm text-yellow-600 ml-2">
            Typically takes 24-72 hours. You'll receive an email when ready.
          </span>
        </div>
      )}

      {/* Processing status banner */}
      {processingStatus && processingStatus.processing > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium text-blue-800">Generating previews...</span>
              <span className="text-sm text-blue-600 ml-2">
                {processingStatus.with_proxy} of {processingStatus.total} ready
                {processingStatus.queue.active > 0 && ` · ${processingStatus.queue.active} active`}
                {processingStatus.queue.queued > 0 && ` · ${processingStatus.queue.queued} queued`}
              </span>
            </div>
            <div className="w-32 bg-blue-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all"
                style={{ width: `${processingStatus.total > 0 ? (processingStatus.with_proxy / processingStatus.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* File grid */}
      {files.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mb-6">
          {files.map((file, idx) => (
            <div
              key={file.id}
              className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all"
              onClick={() => setLightboxIdx(idx)}
            >
              {file.proxy_url ? (
                <img src={file.proxy_url} alt={file.original_filename} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-1">
                  {file.status === 'processing_proxy' || file.status === 'pending' ? (
                    <>
                      <span className="w-6 h-6 border-2 border-blue-300 border-t-blue-500 rounded-full animate-spin" />
                      <span className="text-xs text-blue-400">Processing</span>
                    </>
                  ) : file.status === 'uploading' ? (
                    <>
                      <span className="w-6 h-6 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                      <span className="text-xs text-gray-400">Uploading</span>
                    </>
                  ) : (
                    <>
                      <FiImage size={24} />
                      <span className="text-xs text-gray-400">No preview</span>
                    </>
                  )}
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <p className="text-white text-xs truncate">{file.original_filename}</p>
                <p className="text-white/70 text-xs">{formatBytes(file.file_size_bytes)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-400">
          <FiImage size={32} className="mx-auto mb-2" />
          <p>No files yet. Click Upload to add files.</p>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex justify-center gap-2 mb-6">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => fetchCollection(page)}
              className={`px-3 py-1 text-sm rounded ${
                page === pagination.page ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {page}
            </button>
          ))}
        </div>
      )}

    </div>

      {/* BatchUploader modal, reuse existing upload component */}
      {showUploader && <BatchUploader vaultCollectionId={id}
        onUploadComplete={handleUploadComplete}
        onRequestClose={() => setShowUploader(false)}/>}

      {/* Lightbox */}
      {lightboxIdx !== null && files[lightboxIdx] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxIdx(null)}
        >
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightboxIdx(null)}>
            <FiX size={24} />
          </button>
          <div className="max-w-4xl max-h-[90vh] relative" onClick={e => e.stopPropagation()}>
            {files[lightboxIdx].proxy_url ? (
              <img src={files[lightboxIdx].proxy_url} alt="" className="max-w-full max-h-[85vh] object-contain" />
            ) : (
              <div className="w-64 h-64 flex items-center justify-center bg-gray-800 rounded-lg">
                <FiImage size={48} className="text-gray-500" />
              </div>
            )}
            <div className="text-center mt-3">
              <p className="text-white text-sm">{files[lightboxIdx].original_filename}</p>
              <p className="text-white/60 text-xs mt-1">
                {formatBytes(files[lightboxIdx].file_size_bytes)}
                {files[lightboxIdx].width ? ` · ${files[lightboxIdx].width}×${files[lightboxIdx].height}` : ''}
              </p>
            </div>
          </div>
          {lightboxIdx > 0 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-3xl"
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx - 1); }}
            >
              &#8249;
            </button>
          )}
          {lightboxIdx < files.length - 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white text-3xl"
              onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx + 1); }}
            >
              &#8250;
            </button>
          )}
        </div>
      )}
    </>
  );
}
